import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import imgReplacementIntervals from '../../assets/news-events/replacement_intervals/Capa.png';
import imgReplacementIntervals1 from '../../assets/news-events/replacement_intervals/Imagem 1.png';
import imgReplacementIntervals2 from '../../assets/news-events/replacement_intervals/Imagem 2.png';
import imgReplacementIntervals3 from '../../assets/news-events/replacement_intervals/Imagem 3.png';
import imgReplacementIntervals4 from '../../assets/news-events/replacement_intervals/Imagem 4.png';
import imgReplacementIntervals5 from '../../assets/news-events/replacement_intervals/Imagem 5.png';
import MagazineStageSimple from '../../Components/MagazineStageSimple/MagazineStageSimple';
import MagazineTextBlock from '../../Components/MagazineTextBlock/MagazineTextBlock';

const ReplacementIntervals = () => {
    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to top on component mount
    }, []);

    const { t } = useTranslation();
    const ogTitle = t('Hydraulic components: how to determine replacement intervals');
    const ogDescription = t('Learn how to determine the replacement intervals of hydraulic components and avoid damage to hydraulic systems and sudden failures of safety-relevant components.');
    const ogImage = `https://www.hansa-flex.com.br/${imgReplacementIntervals}`;
    const ogUrl = `https://www.hansa-flex.com.br/hydraulic-components-replacement-intervals`;

    return (
        <div>
            <Helmet>
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:type" content="News" />
            </Helmet>

            <MagazineStageSimple
                imageSrc={imgReplacementIntervals}
                imageAlt="Hydraulic components: how to determine replacement intervals"
                meta="May 27, 2026 | Translation and adaptation: Gabriela Nascimento | Text: Sven Bötjer"
                title={"Hydraulic components: how to determine replacement intervals"}
                text={"Paying attention to how the replacement intervals of hydraulic components are determined helps prevent damage to hydraulic systems and sudden failures of safety-relevant components. This guide explains what should be considered in practice."}
                readingTimeMinutes={8}
            />

            <MagazineTextBlock
                paragraphs={[
                    "The replacement interval indicates how long a component can operate before it needs to be replaced, even if it still appears to be in good condition and working normally.",
                    "This preventive replacement helps avoid unexpected downtime, material failures, and risk situations for the team. In addition to increasing system availability, it contributes directly to the safety of those who operate and maintain the equipment.",
                    "A practical example: in a hydraulic press, the hose may look in good condition on the outside, but it may already have suffered internal aging due to operating time, working pressure, and temperature variations. By scheduling the preventive replacement, the company prevents the failure from appearing at the worst moment: during production, with the risk of unexpected downtime, oil leakage, and exposure of operators to a dangerous situation.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgReplacementIntervals1}
                imageAlt="Assessment of replacement intervals"
                title="Assessment of replacement intervals"
                boldPara="Understand the operating conditions"
                paragraphs={[
                    "Before defining or reviewing a replacement interval, it is important to understand the environment in which the hydraulic components are working. Dust, humidity, temperature, chemicals, and mechanical stress can directly influence the service life of the components.",
                    "Questions that help with the assessment:",
                ]}
                bulletPoints={[
                    "Are the components installed in heavy construction machinery, street cleaning vehicles, agricultural equipment, or machines that operate in environments with dust, humidity, fertilizers, manure, chemicals, mud, sand, gravel, or other abrasive materials?",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgReplacementIntervals2}
                imageAlt="Identify the main risk factors"
                title="Identify the main risk factors"
                bulletPoints={[
                    "Dust: in very dusty environments, such as construction sites, valves and other components can accumulate dirt and become clogged more easily. This increases wear and may require more frequent replacements.",
                    "Corrosion: components that work in corrosive environments, such as coastal areas, aggressive industrial environments, or chemicals in agriculture, are more prone to rust and material failures. Pipes, seals, and valves need special attention.",
                    "Mechanical influences: impacts from stones, sand, gravel, vibrations, and stress caused by use on dirt roads or uneven terrain can also damage the components. Watch out for scratches in the paint, dents, impact marks, and other signs that may reduce the service life of the system.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgReplacementIntervals3}
                imageAlt="Assess at the right frequency"
                title="Assess at the right frequency"
                paragraphs={[
                    "When components operate in corrosive or very dusty environments, the replacement intervals should be checked more frequently, even if there is no apparent damage. Regular inspections help identify risks before they turn into failures.",
                    "Attention: components frequently exposed to sea air, mud, fertilizers, manure, humidity, or abrasive materials should be inspected after periods of intense work, especially in agricultural operations, construction, mining, or coastal environments.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgReplacementIntervals4}
                imageAlt="Adjust the replacement interval when necessary"
                title="Adjust the replacement interval when necessary"
                bulletPoints={[
                    "Dusty environments: valves and seals used in very dusty locations often need to be replaced at shorter intervals than those used in conventional environments. This helps prevent failures caused by wear or dirt accumulation.",
                    "Recommendation for corrosive environments: in severe environments, such as maritime applications or agricultural equipment, it is recommended to reduce inspection or replacement intervals by 20 to 30% due to the higher risk of corrosion.",
                    "Tip for mechanical loads: components frequently exposed to impacts, vibrations, abrasive materials, or other mechanical stress should undergo a more detailed analysis. When necessary, the replacement interval should be shortened before a hydraulic pipe or hose bursts and causes oil leakage, soil contamination, extra costs, and possible environmental risks.",
                ]}
            />

            <MagazineTextBlock title="Recommended inspection points"
                bulletPoints={[
                    "Seals, hydraulic hoses, and pipes should be checked regularly to identify cracks, corrosion, and wear.",
                    "Consider the risk assessments and the replacement intervals recommended by the manufacturer.",
                    "For valves and pumps, check for dirt contamination and residue buildup.",
                    "Check all components for mechanical damage, such as abrasion, blisters, kinks, dents, or cracks caused by impacts, vibrations, or shock loads.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgReplacementIntervals5}
                imageAlt="Hydraulic components: how to determine replacement intervals"
                paragraphs={[
                    "Attention: do not rely only on the intervals indicated in the operating manuals of the machines or systems. In more severe applications, especially in corrosive environments, these deadlines may need to be significantly reduced. This is even more important when standard components are used in maritime applications.",
                    "HANSA-FLEX can support your team in choosing the most suitable hydraulic components for challenging environments, helping to balance safety, service life, and cost-effectiveness in the operation.",
                ]}
                buttonName="Contact us"
                buttonLink="/contact-us"
            />

            <div style={{ backgroundColor: '#f8f8f8' }}>
                <br />
                <br />
                <br />
            </div>
        </div>
    )
}

export default ReplacementIntervals
